import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, Image as ImageIcon, Loader2, Camera, AlertCircle } from 'lucide-react';

const ImageUpload = ({ onImageUpload, existingImage, className, shape = "square" }) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(existingImage || null);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      await handleFileUpload(file);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();
      setPreview(data.url);
      onImageUpload(data.url);
    } catch (error) {
      setError('Failed to upload image. Please try again.');
      console.error('Upload error:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeImage = () => {
    setPreview(null);
    setError('');
    onImageUpload('');
  };

  const containerClasses = `
    relative 
    ${shape === 'circle' ? 'rounded-full' : 'rounded-xl'}
    ${className || ''}
  `;

  return (
    <div className="space-y-2">
      <div
        className={containerClasses}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <AnimatePresence mode="wait">
          {preview ? (
            <motion.div
              key="preview"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="relative group"
            >
              <img 
                src={preview} 
                alt="Preview" 
                className={`
                  w-full h-full object-cover
                  ${shape === 'circle' ? 'rounded-full' : 'rounded-xl'}
                  border-4 border-white shadow-lg
                `}
              />
              <div className={`
                absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100
                transition-opacity duration-200 flex items-center justify-center
                ${shape === 'circle' ? 'rounded-full' : 'rounded-xl'}
              `}>
                <div className="space-x-2">
                 
                  <button
                    type="button"
                    onClick={removeImage}
                    className="p-2 bg-white rounded-full text-gray-700 hover:text-red-600 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="upload"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <label className={`
                relative flex flex-col items-center justify-center w-full h-40
                border-2 border-dashed rounded-xl cursor-pointer
                transition-all duration-200
                ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-500'}
              `}>
                {loading ? (
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto" />
                    <span className="mt-2 text-sm text-gray-500">Uploading...</span>
                  </div>
                ) : (
                  <>
                    <div className="text-center">
                      <div className="p-3 bg-blue-50 rounded-full inline-block mb-2">
                        <Upload className="h-6 w-6 text-blue-500" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-gray-700">
                          {isDragging ? 'Drop image here' : 'Drop image here or click to upload'}
                        </p>
                        <p className="text-xs text-gray-500">
                          Maximum file size: 5MB
                        </p>
                      </div>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e.target.files[0])}
                      disabled={loading}
                    />
                  </>
                )}
              </label>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Error Message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center text-red-600 text-sm"
          >
            <AlertCircle className="h-4 w-4 mr-1.5" />
            {error}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ImageUpload;